import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobListings as mockJobs } from '../mockData';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { MapPin, Briefcase, DollarSign, Clock, Users, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import * as api from '../lib/api';

const JobDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getJobById(id);
        setJob(data);
      } catch {
        const found = mockJobs.find(j => j.id === id);
        setJob(found || null);
      } finally { setLoading(false); }
    };
    load();
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading...</div>;
  if (!job) return <div className="min-h-screen flex items-center justify-center"><div className="text-center"><h2 className="text-2xl font-bold text-gray-900 mb-4">Job Not Found</h2><Button onClick={() => navigate('/jobs')}>Back to Jobs</Button></div></div>;

  const pay = job.payRange || job.pay_range || { min: 0, max: 0 };

  return (
    <div className="min-h-screen bg-[#F9FBF5] pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Button variant="ghost" className="mb-6" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>

        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-8">
          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-6">
            <img src={job.companyLogo || job.company_logo || `https://ui-avatars.com/api/?name=${encodeURIComponent(job.companyName || job.company_name || 'C')}&background=6366F1&color=fff`} alt="" className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg" />
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{job.title}</h1>
              <p className="text-lg text-gray-600 mb-4">{job.companyName || job.company_name}</p>
              <div className="flex flex-wrap gap-3 sm:gap-6 text-sm text-gray-600">
                <span className="flex items-center gap-1"><DollarSign className="w-4 h-4" />${pay.min}-${pay.max}/hr</span>
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{job.location}</span>
                <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" />{job.type}</span>
                <span className="flex items-center gap-1"><Users className="w-4 h-4" />{job.applicationsCount || job.applications_count || 0} applicants</span>
                <span className="flex items-center gap-1"><Clock className="w-4 h-4" />Posted {job.createdAt || job.created_at || 'recently'}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">{(job.skills || []).map((s, i) => (<Badge key={i} variant="secondary" className="text-sm">{s}</Badge>))}</div>

          <div className="mb-8"><h2 className="text-xl font-semibold text-gray-900 mb-4">Job Description</h2><p className="text-gray-700 leading-relaxed whitespace-pre-line">{job.description}</p></div>

          {(job.requirements || []).length > 0 && (
            <div className="mb-8"><h2 className="text-xl font-semibold text-gray-900 mb-4">Requirements</h2><ul className="list-disc list-inside space-y-2 text-gray-700">{job.requirements.map((r, i) => (<li key={i}>{r}</li>))}</ul></div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
            <Button className="bg-[#0A3900] hover:bg-[#1A5C0A] flex-1 sm:flex-none" onClick={() => {
              if (!user) { 
                // Trigger login when applying, like Mercor
                toast({ title: 'Sign in required', description: 'Please sign in to apply for this job.' }); 
                setTimeout(() => {
                  window.location.href = '/login';
                }, 1000);
                return; 
              }
              // Navigate to apply page with correct jobId parameter
              navigate(`/candidate/apply/${job.id || job.job_id}`);
            }}>Apply Now</Button>
            <Button variant="outline">Save Job</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailPage;
